import React, { useState, useEffect } from 'react';
import { useMultiHOA } from './MultiHOAContext';
import api from './api';
import { UsersIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

const CommunityInfoPanel = () => {
  const { activeHoaId } = useMultiHOA();
  const [boardMembers, setBoardMembers] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (activeHoaId) {
      loadCommunityInfo();
    }
  }, [activeHoaId]);

  const loadCommunityInfo = async () => {
    try {
      setLoading(true);
      
      // Load board members
      const { data: boardData } = await api.get('/api/board/members');
      setBoardMembers(boardData.board_members || []);
      
      // Load committees
      const currentHoaId = activeHoaId;
      const { data: committeeData } = await api.get(`/api/hoa/${currentHoaId}/committees`);
      setCommittees(committeeData.committees || []);
    } catch (error) {
      console.error('Error loading community info:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <aside className="w-full lg:w-80 shrink-0 p-4 lg:border-l bg-white">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="w-full lg:w-80 shrink-0 p-4 lg:border-l bg-white overflow-y-auto">
      <div className="space-y-6">
        {/* Board Members */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheckIcon className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">Board Members</h3>
          </div>
          <div className="space-y-2">
            {boardMembers.length === 0 ? (
              <p className="text-sm text-gray-500">No board members</p>
            ) : (
              boardMembers.map((member, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="font-medium text-sm text-gray-900">
                    {member.first_name} {member.last_name}
                  </div>
                  {member.role && (
                    <div className="text-xs text-gray-600 capitalize">
                      {member.role.replace('_', ' ')}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Committees */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <UsersIcon className="h-5 w-5 text-purple-600" />
            <h3 className="font-semibold text-gray-900">Committees</h3>
          </div>
          <div className="space-y-2">
            {committees.length === 0 ? (
              <p className="text-sm text-gray-500">No committees</p>
            ) : (
              committees.map((committee) => (
                <div key={committee.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="font-medium text-sm text-gray-900 mb-2">
                    {committee.name}
                  </div>
                  {committee.description && (
                    <div className="text-xs text-gray-600 mb-2">
                      {committee.description}
                    </div>
                  )}
                  {committee.members && committee.members.length > 0 ? (
                    <div className="space-y-1.5 mt-2">
                      {committee.members.map((member, idx) => (
                        <div key={idx} className="pl-2 border-l-2 border-gray-300">
                          <div className="text-xs font-medium text-gray-800">
                            {member.name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Member'}
                          </div>
                          {member.role && (
                            <div className="text-xs text-gray-600 capitalize">
                              {member.role.replace('_', ' ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 mt-2">No members yet</div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default CommunityInfoPanel;
